/*
  Cart partial checkout — DOM wiring

  🔴 The cart re-renders its whole innerHTML on any mutation, even a quantity
     bump (cart.js:480~504). The checkbox DOM is destroyed every time, so the
     selection is held here and repainted after cart:updated.
*/

/* 🔴 Never `import ... from './cart-selection-state.js'`. A relative specifier
   resolves to a URL with no version query, which the CDN caches for a year, so
   a re-upload still serves the old file and the module dies on its exports.
   The section passes the versioned URL instead. */
const selectionStateSrc = document.querySelector('script[data-selection-state-src]')?.dataset
  .selectionStateSrc;

/* No relative fallback — that is the trap above, and falling back to it would
   quietly restore the bug. */
if (!selectionStateSrc) {
  throw new Error(
    'cart-partial-checkout: script tag is missing data-selection-state-src. ' +
      'The section must pass the versioned asset_url for cart-selection-state.js.'
  );
}

const {
  pruneDeselected,
  isAllSelected,
  getSelectedKeys,
  lineTotal,
  selectedSubtotal,
  buildCartInput,
  quantitiesMatch,
} = await import(selectionStateSrc);

if (!window.__verishCartPartialCheckout) {
  window.__verishCartPartialCheckout = true; /* drawer + cart page load this twice */

  /* 🔴 Selection lives here and nowhere else — every page load starts fully
     selected, matching what the server renders. An earlier version kept it in
     sessionStorage, which meant the server drew all boxes checked and JS then
     unchecked some once the cart arrived: the boxes visibly flicked off. */
  const state = {
    cart: null,
    deselected: new Set(),
    /* key → quantity the shopper just asked for, until the server confirms it.
       cart.js updates the quantity box immediately but leaves every money figure
       on the previous quantity until the round trip finishes (~1.6s), so the
       totals read as one unit short. These let us fill that gap. */
    pendingQty: new Map(),
  };

  const lineKeys = () => (state.cart ? state.cart.items.map((i) => i.key) : []);

  /* Repaint the checkboxes from state. Called after every re-render. */
  function paint() {
    const keys = lineKeys();

    document.querySelectorAll('[data-cart-line-select]').forEach((box) => {
      box.checked = !state.deselected.has(box.dataset.key);
    });

    const allSelected = isAllSelected(state.deselected, keys);
    const noneSelected = getSelectedKeys(state.deselected, keys).length === 0;

    document.querySelectorAll('[data-cart-select-all]').forEach((box) => {
      box.checked = allSelected;
      box.indeterminate = !allSelected && !noneSelected;
    });

    paintLineTotals();
    paintSubtotal();

    const empty = noneSelected;
    document.querySelectorAll('[data-checkout-btn]').forEach((button) => {
      button.classList.toggle('is-disabled', empty);
      button.setAttribute('aria-disabled', empty ? 'true' : 'false');
    });
  }

  /* Fill in each line's Total from the quantity the shopper just asked for.
     The theme leaves this cell on the old quantity until the server answers,
     so without this the row reads one unit short for over a second.

     Only rendered on the cart page at desktop width — the drawer has no such
     cell, and below 1024px it is hidden. querySelector simply finds nothing. */
  function paintLineTotals() {
    const theme = window.FoxTheme;
    if (!state.cart || !state.pendingQty.size || !theme || !theme.Currency || !theme.settings) return;

    state.cart.items.forEach((item) => {
      if (!state.pendingQty.has(item.key)) return;
      const row = document.querySelector(`[data-key="${CSS.escape(item.key)}"]`);
      /* 🔴 Write into [data-cart-line-total], not the cell. Replacing the cell
         drops the weight class the template puts inside it. The template owns
         the markup; this owns the text. */
      const amount = row && row.closest('.cart-item')?.querySelector('[data-cart-line-total]');
      if (!amount) return;
      const text = theme.Currency.formatMoney(lineTotal(item, state.pendingQty), theme.settings.moneyFormat);
      if (amount.textContent.trim() !== text) amount.textContent = text;
    });
  }

  /* The amount that belongs on screen right now, or null when the cart has not
     arrived yet and there is nothing to say. Same formatMoney call the theme
     uses, so the figure keeps matching the server-rendered one — only the
     amount differs once lines are deselected. */
  function subtotalText() {
    const theme = window.FoxTheme;
    if (!state.cart || !theme || !theme.Currency || !theme.settings) return null;

    /* All selected → use the server total, which already carries cart-level
       discounts that summing line prices would miss. */
    const allSelected = isAllSelected(state.deselected, lineKeys());
    const cents = allSelected && !state.pendingQty.size
      ? state.cart.total_price
      : selectedSubtotal(state.cart, state.deselected, state.pendingQty);
    return theme.Currency.formatMoney(cents, theme.settings.moneyFormat);
  }

  /* 🔴 The single owner of the subtotal text. cart.js updates dataset.cents and
     delegates drawing here (cart.js:writeSubtotal). When both wrote, the full
     cart total sat where the selected amount belonged for over a second. */
  window.FoxThemeSubtotalRenderer = (el) => {
    const text = subtotalText();
    if (text !== null && el.innerHTML !== text) el.innerHTML = text;
  };

  /* textOnly skips dataset.cents — that figure must stay on the full cart total
     because cart.js reads it as the base for its optimistic arithmetic. */
  function paintSubtotal(textOnly) {
    const text = subtotalText();
    if (text === null) return;
    const fullCents = state.cart.total_price;

    document.querySelectorAll('.totals__subtotal-value').forEach((el) => {
      /* Compare before writing — no point churning the DOM on every repaint. */
      if (el.innerHTML !== text) el.innerHTML = text;
      /* 🔴 dataset.cents stays on the FULL total on purpose. cart.js reads it
         (cart.js:752) as the base for its optimistic add/subtract math, so
         storing the reduced figure here would compound into a wrong total. */
      if (!textOnly && el.dataset.cents !== String(fullCents)) {
        el.dataset.cents = String(fullCents);
      }
    });
  }

  /* 🔴 Do not put a MutationObserver on this element.

     cart.js also writes this element optimistically (cart.js:760~782), so an
     obvious-looking fix is to watch it and put our figure back. That breaks the
     cart: with a line deselected, quantity updates stop rendering entirely —
     the server takes the new quantity while the row keeps showing the old one.
     Observing a node the theme rewrites during its own render reenters that
     render, and cart.js drops the result.

     The quantity listener below covers the same ground safely: it runs after
     cart.js's own change handler, so our figure lands on top of the optimistic
     one without ever touching the render in progress. */

  /* Take the server cart as truth and drop keys it no longer has. */
  function adopt(cart) {
    state.cart = cart;
    state.deselected = pruneDeselected(state.deselected, lineKeys());
    state.pendingQty.clear(); /* the server has spoken; guesses are obsolete */
    clearTimeout(reconcileTimer);
    paint();
  }

  /* 🔴 A guess must be confirmed or dropped. A refused change — 422 at the
     stock cap, 429 under load — publishes no cart update, so the guess stayed
     and every refusal piled on. Setting one schedules a reconcile; success
     clears it and cancels the timer, so the happy path costs nothing and no
     failure mode needs recognising. */
  const RECONCILE_MS = 2500; /* comfortably past the 1.2-1.8s success round-trip */
  let reconcileTimer = null;

  function scheduleReconcile() {
    clearTimeout(reconcileTimer);
    reconcileTimer = setTimeout(() => {
      if (state.pendingQty.size) loadCart();
    }, RECONCILE_MS);
  }

  /* Returns whether state.cart now reflects the server. The checkout path needs
     to know — sending a stale cart charges the wrong quantities. */
  async function loadCart() {
    try {
      const res = await fetch(`${window.Shopify.routes.root}cart.js`);
      if (!res.ok) return false;
      adopt(await res.json());
      return true;
    } catch (e) {
      return false; /* Leave everything selected. The existing checkout flow still works. */
    }
  }

  document.addEventListener('change', (event) => {
    const box = event.target;

    if (box.matches('[data-cart-line-select]')) {
      if (box.checked) state.deselected.delete(box.dataset.key);
      else state.deselected.add(box.dataset.key);
      paint();
      return;
    }

    if (box.matches('[data-cart-select-all]')) {
      state.deselected = box.checked ? new Set() : new Set(lineKeys());
      paint();
    }
  });

  /* cart.js writes the new quantity into the input and fires change on it, both
     for the +/− buttons and for typed values — so one listener covers every way
     of changing a quantity.

     🔴 Bubble phase, and money only. An earlier version ran on capture and
        called the full paint(), which reset dataset.cents to the pre-change
        total before cart.js had read it. That corrupted the theme's optimistic
        bookkeeping badly enough that whole quantity updates stopped rendering —
        the server had the new quantity while the row still showed the old one.
        Let cart.js do its work first; we only repaint the figures afterwards. */
  document.addEventListener('change', (event) => {
    const input = event.target;
    if (!input.matches || !input.matches('input[name="updates[]"]')) return;

    const row = input.closest('[data-key]');
    const key = row && row.dataset.key;
    const wanted = Number(input.value);
    if (!key || !Number.isFinite(wanted) || wanted < 0) return;

    state.pendingQty.set(key, wanted);
    scheduleReconcile();
    paintLineTotals();
    paintSubtotal(true);
  });

  /* cart.js also fires this during optimistic updates, where the payload is
     only { item_count, _optimistic } — refetch the real cart in that case. */
  document.addEventListener('cart:updated', (event) => {
    const cart = event.detail && event.detail.cart;
    if (cart && !cart._optimistic && Array.isArray(cart.items)) adopt(cart);
    else loadCart();
  });

  const API_VERSION = '2025-10';
  const FETCH_TIMEOUT = 5000;

  /* 🔴 @inContext carries the current market. Without it a TW cart priced at
     950 TWD checks out at 34 USD (measured 2026-08-04). If the market is
     unknown we hand the click back to the normal checkout rather than risk
     charging in the wrong currency. */
  /* 🔴 Ask for lines back and compare them. cartCreate reduces a line past the
     available stock without reporting a userError, so trusting checkoutUrl
     alone sent someone who picked seven to a checkout for three. */
  const MUTATION = `
    mutation PartialCheckout($input: CartInput!, $country: CountryCode!, $language: LanguageCode!)
    @inContext(country: $country, language: $language) {
      cartCreate(input: $input) {
        cart {
          checkoutUrl
          lines(first: 250) {
            nodes {
              quantity
              merchandise { ... on ProductVariant { id } }
            }
          }
        }
        userErrors { field message }
      }
    }`;


  function setBusy(button, busy) {
    button.setAttribute('aria-busy', busy ? 'true' : 'false');
    button.classList.toggle('is-cart-busy', busy); /* reuses cart.js's busy look */
  }

  function announce(button, message) {
    let note = button.parentElement.querySelector('[data-cart-select-error]');
    if (!note) {
      note = document.createElement('p');
      note.className = 'cart-select-error';
      note.setAttribute('role', 'status');
      note.setAttribute('data-cart-select-error', '');
      button.parentElement.appendChild(note);
    }
    note.textContent = message || '';
  }

  async function createPartialCart(country, language) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT);
    try {
      /* 🔴 Do not prefix routes.root. On localized paths that yields
         /tw/api/... which does not exist — the Storefront API is always at
         the domain root. No access token: same-origin calls are allowed. */
      const input = buildCartInput(state.cart, state.deselected);
      const res = await fetch(`/api/${API_VERSION}/graphql.json`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        signal: controller.signal,
        body: JSON.stringify({
          query: MUTATION,
          variables: { country, language, input },
        }),
      });
      if (!res.ok) return null;
      const json = await res.json();
      const payload = json.data && json.data.cartCreate;
      if (!payload || (payload.userErrors && payload.userErrors.length)) return null;
      const nodes = payload.cart && payload.cart.lines && payload.cart.lines.nodes;
      if (!payload.cart || !quantitiesMatch(input.lines, nodes)) return { shortfall: true };
      return { checkoutUrl: payload.cart.checkoutUrl };
    } catch (e) {
      return null; /* aborted or network failure */
    } finally {
      clearTimeout(timer);
    }
  }

  /* 🔴 CAPTURE phase, not bubble. layout/theme.liquid:476 binds a click handler
     directly on the checkout button that hard-navigates to /checkout/, and a
     handler on the element itself runs before any delegated one on document.
     Capturing lets us stop that navigation before it starts. When everything
     is selected we do nothing and let the existing flow run untouched. */
  document.addEventListener(
    'click',
    async (event) => {
      const button = event.target.closest('[data-checkout-btn]');
      if (!button || !state.cart) return;

      const keys = lineKeys();
      if (isAllSelected(state.deselected, keys)) return; /* normal checkout */

      event.preventDefault();
      event.stopPropagation();

      if (!getSelectedKeys(state.deselected, keys).length) {
        announce(button, button.dataset.emptyError);
        return;
      }

      /* 🔴 CountryCode/LanguageCode are GraphQL enums, so they must be upper
         case with underscores. Liquid hands us a lower-case, hyphenated locale
         ("en", "pt-br"); passing it through fails with "provided invalid
         value" and the checkout silently falls over. */
      const country = (button.dataset.country || '').toUpperCase();
      const language = (button.dataset.checkoutLanguage || '').toUpperCase().replace(/-/g, '_');
      if (!country || !language) {
        window.location.assign(button.getAttribute('href')); /* fall back to full checkout */
        return;
      }

      setBusy(button, true);
      announce(button, '');

      /* 🔴 Do not build the request from state.cart. The quantity buttons paint
         the new number before the server confirms it, and checking out in that
         window sent the old quantity. Re-read the cart at click time. */
      const refreshed = await loadCart();
      if (!refreshed) {
        setBusy(button, false);
        announce(button, button.dataset.checkoutError);
        return;
      }

      /* Re-check the selection against the cart we just read — another tab may
         have removed a line meanwhile. */
      const freshKeys = lineKeys();
      if (isAllSelected(state.deselected, freshKeys)) {
        window.location.assign(button.getAttribute('href')); /* all selected */
        return;
      }
      if (!getSelectedKeys(state.deselected, freshKeys).length) {
        setBusy(button, false);
        announce(button, button.dataset.emptyError);
        return;
      }

      const result = await createPartialCart(country, language);
      setBusy(button, false);

      if (result && result.checkoutUrl) window.location.assign(result.checkoutUrl);
      else if (result && result.shortfall) announce(button, button.dataset.stockError);
      else announce(button, button.dataset.checkoutError);
    },
    true
  );

  loadCart();
}
